import { getServerCookieValues } from "@/lib/utils/cookie-utils";
import { normalizeCode, normalizeMessage, normalizeStatus, getGqlSignatures } from "@/lib/utils/common-utils";

export type OperationSignatureValue = string | number | string[];

export type CookieValues = {
  rxVisitor?: string;
  traceId_QRV?: string;
  sessionID_QRV?: string;
  MI_SITE?: string;
  languageCode?: string;
};

export type ErrorLogParams = {
  alertName: string;
  error: unknown;
  pageName?: string;
  apiName?: string;
  httpMethod?: string;
  correlationId?: string | string[] | number;
  operation?: string | number;
  operationSignature?: OperationSignatureValue;
  redirectTo?: string;
};

export type ErrorLogData = {
  alertName: string;
  timestamp: string;
  pageName?: string;
  logLevel: string;
  errCode: string | number;
  status?: string | number;
  errMessage: string;
  sessionID_QRV: string;
  traceId_QRV: string;
  rxVisitor: string;
  MI_SITE: string;
  languageCode?: string;
  apiName: string;
  httpMethod: string;
  correlationId: string | string[] | number;
  operation: string | number;
  operationSignature: OperationSignatureValue;
  redirectTo: string;
};

/**
 * Normalizes cookie values, providing defaults for missing values
 * Ensures that undefined values are converted to empty strings, not "undefined"
 */
export const normalizeCookieValues = (cookieVals?: CookieValues): CookieValues => {
  if (!cookieVals) {
    return {
      rxVisitor: '',
      traceId_QRV: '',
      sessionID_QRV: '',
      MI_SITE: '',
      languageCode: '',
    };
  }

  return {
    rxVisitor: cookieVals.rxVisitor ?? '',
    traceId_QRV: cookieVals.traceId_QRV ?? '',
    sessionID_QRV: cookieVals.sessionID_QRV ?? '',
    MI_SITE: cookieVals.MI_SITE ?? '',
    languageCode: cookieVals.languageCode ?? '',
  };
};

/**
 * Resolves cookie values from multiple sources with fallback order
 */
export const resolveCookieValues = (options: {
  provided?: CookieValues;
  windowValues?: CookieValues;
  headerSource?: string;
}): CookieValues => {
  // Priority 1: Explicitly provided cookie values
  if (options.provided) {
    return options.provided;
  }

  // Priority 2: Cookie values from window object
  if (options.windowValues) {
    return options.windowValues;
  }

  // Priority 3: Cookie values from server/header
  if (options.headerSource) {
    const cookieValues = getServerCookieValues(options.headerSource);
    // Debug logging to see what cookies were extracted
    console.debug('[Cookie Resolution] Extracted cookies:', {
      headerLength: options.headerSource.length,
      extractedValues: cookieValues,
      headerPreview: options.headerSource.substring(0, 100) + '...'
    });
    return cookieValues;
  }

  return {};
};

/**
 * Resolves operation signature from parameter or GQL signatures map
 */
export const resolveOperationSignature = (
  operationSignature?: OperationSignatureValue,
  operation?: string | number,
): OperationSignatureValue => {
  if (operationSignature) {
    return operationSignature;
  }

  const gqlSignatures = getGqlSignatures();
  return gqlSignatures[operation as string] as OperationSignatureValue ?? '';
};

/**
 * Builds error log data object with all normalized values
 */
export const buildErrorLogData = (
  params: ErrorLogParams,
  cookieValues: CookieValues,
  options: { logLevel?: string } = {},
): Partial<ErrorLogData> => {
  const gqlSignatures = getGqlSignatures();
  const normalizedCookies = normalizeCookieValues(cookieValues);

  return {
    alertName: params.alertName,
    timestamp: new Date().toISOString(),
    pageName: params.pageName,
    logLevel: options.logLevel ?? 'ERROR',
    errCode: normalizeCode(params.error),
    status: normalizeStatus(params.error),
    errMessage: normalizeMessage(params.error),
    sessionID_QRV: normalizedCookies.sessionID_QRV,
    traceId_QRV: normalizedCookies.traceId_QRV,
    rxVisitor: normalizedCookies.rxVisitor,
    MI_SITE: normalizedCookies.MI_SITE,
    languageCode: normalizedCookies.languageCode,
    apiName: params.apiName ?? '',
    httpMethod: params.httpMethod ?? '',
    correlationId: params.correlationId ?? '',
    operation: params.operation ?? '',
    operationSignature: params.operationSignature ?? (gqlSignatures[params.operation as string] as OperationSignatureValue) ?? '',
    redirectTo: params.redirectTo ?? '',
  };
};
